import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  keepPreviousData,
  type QueryClient,
} from '@tanstack/react-query'
import {
  waApi,
  type WAConversationsListParams,
  type WAMessagesListParams,
  type WASendMessageInput,
  type WAUpdateConversationInput,
} from '@/lib/api-client'
import { toast } from 'sonner'

// ============================================================================
// Query Keys
// ============================================================================

export const waQueryKeys = {
  all: ['wa'] as const,
  conversations: () => [...waQueryKeys.all, 'conversations'] as const,
  conversationList: (params?: WAConversationsListParams) =>
    [...waQueryKeys.conversations(), 'list', params] as const,
  conversationStats: () => [...waQueryKeys.conversations(), 'stats'] as const,
  conversation: (id: number) => [...waQueryKeys.conversations(), id] as const,
  messages: (conversationId: number) =>
    [...waQueryKeys.conversation(conversationId), 'messages'] as const,
  messageList: (conversationId: number, params?: WAMessagesListParams) =>
    [...waQueryKeys.messages(conversationId), params] as const,
}

// ============================================================================
// Conversations Hooks
// ============================================================================

/**
 * Hook to fetch paginated conversations list
 */
export function useWAConversations(params?: WAConversationsListParams) {
  return useQuery({
    queryKey: waQueryKeys.conversationList(params),
    queryFn: () => waApi.conversations.list(params),
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000, // 10 seconds - conversations update frequently
    refetchInterval: 30 * 1000, // Poll every 30 seconds for new messages
  })
}

/**
 * Hook to fetch infinite conversations list with cursor pagination
 */
export function useWAConversationsInfinite(
  params?: Omit<WAConversationsListParams, 'cursor'>
) {
  return useInfiniteQuery({
    queryKey: waQueryKeys.conversationList(params),
    queryFn: ({ pageParam }) =>
      waApi.conversations.list({ ...params, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000,
  })
}

/**
 * Hook to fetch conversation statistics
 */
export function useWAConversationStats() {
  return useQuery({
    queryKey: waQueryKeys.conversationStats(),
    queryFn: () => waApi.conversations.stats(),
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Poll every minute
  })
}

/**
 * Hook to fetch single conversation
 */
export function useWAConversation(id: number | null) {
  return useQuery({
    queryKey: waQueryKeys.conversation(id!),
    queryFn: () => waApi.conversations.get(id!),
    enabled: id !== null && id > 0,
    staleTime: 10 * 1000,
  })
}

/**
 * Hook to update conversation
 */
export function useUpdateWAConversation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: WAUpdateConversationInput }) =>
      waApi.conversations.update(id, data),

    onSuccess: (_response, variables) => {
      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey: waQueryKeys.conversations() })

      // Invalidate specific conversation
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.conversation(variables.id),
      })
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Gagal kemaskini perbualan')
    },
  })
}

// ============================================================================
// Messages Hooks
// ============================================================================

/**
 * Hook to fetch messages with infinite scroll (cursor-based)
 */
export function useWAMessages(conversationId: number | null) {
  return useInfiniteQuery({
    queryKey: waQueryKeys.messages(conversationId!),
    queryFn: ({ pageParam }) =>
      waApi.conversations.messages(conversationId!, {
        cursor: pageParam as string | undefined,
        direction: 'older',
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    enabled: conversationId !== null && conversationId > 0,
    staleTime: 5 * 1000, // 5 seconds - messages update very frequently
    refetchInterval: 10 * 1000, // Poll every 10 seconds
  })
}

/**
 * Prefetch messages for a conversation (call on hover for instant loading)
 */
export function prefetchWAMessages(queryClient: QueryClient, conversationId: number) {
  queryClient.prefetchInfiniteQuery({
    queryKey: waQueryKeys.messages(conversationId),
    queryFn: () =>
      waApi.conversations.messages(conversationId, {
        direction: 'older',
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    staleTime: 5 * 1000,
  })
}

/**
 * Hook to send message
 */
export function useSendWAMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ conversationId, data }: { conversationId: number; data: WASendMessageInput }) =>
      waApi.conversations.sendMessage(conversationId, data),

    onSuccess: (_response, variables) => {
      // Invalidate messages
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.messages(variables.conversationId),
      })

      // Invalidate conversation (for last message preview)
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.conversation(variables.conversationId),
      })

      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey: waQueryKeys.conversations() })
    },

    onError: (error: Error) => {
      toast.error(error.message || 'Gagal hantar mesej')
    },
  })
}

/**
 * Hook to mark conversation as read
 */
export function useMarkWAAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) =>
      waApi.conversations.markAsRead(conversationId),

    onSuccess: (_response, conversationId) => {
      // Invalidate conversation
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.conversation(conversationId),
      })

      // Invalidate conversations list (for unread count)
      queryClient.invalidateQueries({ queryKey: waQueryKeys.conversations() })

      // Invalidate stats
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.conversationStats(),
      })
    },
  })
}

/**
 * Hook to mark a conversation back to unread (flag for a colleague to follow up)
 */
export function useMarkWAAsUnread() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: number) =>
      waApi.conversations.markAsUnread(conversationId),

    onSuccess: (_response, conversationId) => {
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.conversation(conversationId),
      })
      queryClient.invalidateQueries({ queryKey: waQueryKeys.conversations() })
      queryClient.invalidateQueries({
        queryKey: waQueryKeys.conversationStats(),
      })
    },
  })
}
